import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileBadge2,
  ArrowRight,
  MapPin,
  Layers,
  HardHat,
  Sun,
  Truck,
  Trophy,
  Award,
  ZoomIn,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { SITE, waLink, IMAGES } from "../data/site";

const SITE_URL = SITE.domain;

const TIMELINE = [
  {
    year: "1972",
    title: "The founding",
    text: "Late Naresh Chander Gupta establishes Universal Engineers, becoming an authorized channel partner for M-SEAL (Mahindra Group) — delivering pioneering insulation and sealing solutions to key infrastructure and power projects.",
  },
  {
    year: "2007",
    title: "The 3M partnership",
    text: "Universal Engineers partners with 3M in 2007, when 3M took over Mahindra's Cable Jointing Division (M-SEAL) — continuing seamlessly as an Authorised Channel Partner for 3M Electro & Communication Division.",
  },
  {
    year: "Today",
    title: "A comprehensive solutions provider",
    text: "Over five decades, we have evolved from a pioneering regional distributor into an end-to-end partner — combining world-class technology with deep on-ground technical expertise.",
  },
];

const CAPABILITIES = [
  {
    icon: Layers,
    title: "Comprehensive Product Portfolio",
    text: "Authorized distributor of genuine 3M Jointing Kits & Terminations, LT/HT Tapes, Terminal Protectors, Resin Kits, and Specialty Epoxies.",
  },
  {
    icon: HardHat,
    title: "Expert Installation Services",
    text: "End-to-end installation and site execution delivered by our certified, highly trained technical teams.",
  },
  {
    icon: Sun,
    title: "Sector Focus",
    text: "Tailored electrical infrastructure solutions for Solar, Wind, Conventional Power Generation, and Power Distribution networks.",
  },
  {
    icon: Truck,
    title: "Robust Supply & Support Network",
    text: "An extensive regional dealership network ensuring ready product availability, fast dispatch, and dedicated technical support.",
  },
];

const AWARDS = [
  {
    icon: Trophy,
    title: "Best Sales Performance (North Region)",
    text: "Awarded repeatedly across decades by both Mahindra (M-SEAL) and 3M India.",
  },
  {
    icon: Award,
    title: "Top Channel Partner Accolades",
    text: "Recognized for market leadership, supply chain reliability, and technical excellence in specialized power applications.",
  },
];

const CERTIFICATES = [
  {
    icon: ShieldCheck,
    title: "3M Authorized Distributor Certificate",
    sub: "3M India Limited — 2026",
    img: "/img/certificate-3m.png",
    alt: "3M India Limited Authorized Distributor Certificate 2026 — Universal Engineers",
    testid: "certificate-3m",
  },
  {
    icon: FileBadge2,
    title: "MSME Udyam Registration Certificate",
    sub: "UDYAM-DL-03-0012296 — M/s Universal Engineers",
    img: "/img/certificate-msme.png",
    alt: "MSME Udyam Registration Certificate — M/s Universal Engineers",
    testid: "certificate-msme",
  },
];

const GALLERY = [
  {
    src: "/img/memory-group.jpg",
    alt: "Universal Engineers team with the visiting 3M delegation from the USA",
    caption: "With the visiting 3M delegation from the USA",
  },
  {
    src: "/img/memory-delegates.jpg",
    alt: "Welcoming 3M delegates from the USA with traditional garlands",
    caption: "Welcoming 3M delegates from the USA",
  },
  {
    src: "/img/memory-thailand-group.jpg",
    alt: "Universal Engineers team at the 3M Dealers' Meet in Thailand, 2025",
    caption: "Dealers' Meet, Thailand — 2025",
  },
  {
    src: "/img/memory-award.jpg",
    alt: "Receiving the sales performance award on stage at Elevate 2023",
    caption: "Elevate 2023 — sales award received on stage",
  },
  {
    src: "/img/memory-booth.jpg",
    alt: "Universal Engineers team at the 3M booth during an electrical industry exhibition",
    caption: "At the 3M booth — electrical industry exhibition",
  },
  {
    src: "/img/memory-thailand-trio.jpg",
    alt: "Team members at the 3M Dealers' Meet in Thailand, 2025",
    caption: "Team moments — Dealers' Meet, Thailand 2025",
  },
  {
    src: "/img/memory-conversation.jpg",
    alt: "In conversation with the 3M delegation from the USA",
    caption: "In conversation with the 3M delegation from the USA",
  },
  {
    src: "/img/memory-office-visit.jpg",
    alt: "3M team visit at the Universal Engineers Bhagirath Palace office",
    caption: "3M team visit — Bhagirath Palace office",
  },
];

const About = () => (
  <>
    <Seo
      title="About Us | Universal Engineers — Legacy of Excellence Since 1972"
      description="Founded in 1972 by Late Naresh Chander Gupta, Universal Engineers began as an M-SEAL (Mahindra Group) channel partner and, after 3M's 2007 acquisition of M-SEAL, became an Authorised 3M Channel Partner — end-to-end cable jointing, termination and insulation solutions from LV to 66kV."
      path="/about"
      jsonld={{
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About Universal Engineers",
        url: `${SITE_URL}/about`,
      }}
    />

    <section className="grain relative overflow-hidden bg-navy pb-20 pt-36 sm:pb-24 lg:pt-44" data-testid="about-hero">
      <div className="bp-grid absolute inset-0" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 sm:text-xs">
            <span className="inline-block h-2 w-2 bg-safety" /> About Us — Universal Engineers
          </p>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Legacy of Excellence in Specialised Electrical Solutions
          </h1>
          <p className="mt-6 font-mono text-sm uppercase tracking-[0.3em] text-safety sm:text-base" data-testid="about-hero-since">
            Since 1972
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-20 sm:py-24" data-testid="about-story">
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
              <span className="inline-block h-2 w-2 bg-safety" /> Our Story &amp; Heritage
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              From M-SEAL to 3M — a fifty-year partnership of trust.
            </h2>
            <div className="mt-6 space-y-5 leading-relaxed text-slate-600">
              <p>
                Founded in <strong>1972</strong> by{" "}
                <strong>Late Naresh Chander Gupta</strong>, Universal Engineers
                has built a robust reputation as a trusted name in
                high-performance electrical solutions across India.
              </p>
              <p>
                Our journey began as an authorized channel partner for{" "}
                <strong>M-SEAL (Mahindra Group)</strong>, delivering pioneering
                insulation and sealing solutions to key infrastructure and
                power projects. Following 3M&rsquo;s acquisition of M-SEAL in{" "}
                <strong>2007</strong>, Universal Engineers seamlessly
                transitioned into an Authorised Channel Partner for{" "}
                <strong>3M Electro &amp; Communication Division</strong>.
              </p>
              <p>
                Over five decades, we have evolved from a pioneering regional
                distributor into a comprehensive solutions provider—combining
                world-class technology with deep on-ground technical expertise.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 border-l-2 border-safety bg-safety-tint p-5" data-testid="about-offices">
              <p className="font-display text-sm font-bold text-navy">Our offices</p>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-navy">
                <p className="flex items-start gap-2.5">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-safety" />
                  <span>
                    <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Head Office</span>
                    {SITE.addressLine1}, {SITE.addressLine2}
                  </span>
                </p>
                <p className="flex items-start gap-2.5">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-safety" />
                  <span>
                    <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Branch</span>
                    {SITE.address2Line1}, {SITE.address2Line2}
                  </span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="relative lg:mt-14">
          <div className="absolute -right-4 -top-4 h-full w-full border-2 border-navy" />
          <img src={IMAGES.warehouse} alt="Universal Engineers warehouse stocked with 3M cable accessories" className="clip-notch relative w-full" data-testid="about-story-image" />
        </Reveal>
      </div>
    </section>

    <section className="bg-slate-50 py-20 sm:py-24" data-testid="about-timeline">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
            <span className="inline-block h-2 w-2 bg-safety" /> The Journey
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            Three chapters. One standard.
          </h2>
        </Reveal>
        <div className="mt-12 border-l-2 border-slate-200 pl-8">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.06} className="relative pb-10 last:pb-0">
              <span className="absolute -left-[41px] top-1 h-3.5 w-3.5 border-2 border-safety bg-white" data-testid="timeline-dot" />
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety">{t.year}</p>
              <h3 className="mt-2 font-display text-xl font-bold text-navy">{t.title}</h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">{t.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-24" data-testid="about-what-we-do">
      <div className="mx-auto max-w-4xl px-5 text-left sm:px-8">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
            <span className="inline-block h-2 w-2 bg-safety" /> What We Do
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            End-to-end jointing, termination and insulation — LV to 66kV.
          </h2>
          <div className="mt-6 space-y-5 leading-relaxed text-slate-600">
            <p>
              We specialize in end-to-end cable jointing, termination, and
              insulation solutions spanning Low Voltage (LV) to High Tension
              (HT) systems up to 66kV.
            </p>
            <p>
              By pairing 3M&rsquo;s globally recognized engineering innovations
              with our specialized, hands-on application knowledge, we ensure
              maximum reliability, safety, and operational performance for
              critical power networks.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-2" data-testid="about-voltage-chips">
            {["LV", "1.1kV", "11kV", "22kV", "33kV", "66kV"].map((v) => (
              <span key={v} className="border border-slate-300 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-700">
                {v}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-y border-slate-200 bg-slate-50 py-20 sm:py-24" data-testid="about-capabilities">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
            <span className="inline-block h-2 w-2 bg-safety" /> Core Capabilities at a Glance
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            What sets us apart.
          </h2>
        </Reveal>
        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" delay={0.04}>
          {CAPABILITIES.map((c) => (
            <StaggerItem key={c.title}>
              <div className="group flex h-full flex-col border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:border-safety hover:shadow-xl" data-testid="capability-card">
                <div className="flex h-12 w-12 items-center justify-center border border-slate-200 transition-colors group-hover:border-safety group-hover:bg-safety-tint">
                  <c.icon size={22} className="text-navy transition-colors group-hover:text-safety" strokeWidth={1.6} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold leading-snug text-navy">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{c.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <section className="py-20 sm:py-24" data-testid="about-awards">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
            <span className="inline-block h-2 w-2 bg-safety" /> Awards &amp; Industry Recognition
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            Excellence is at the heart of our operations.
          </h2>
          <p className="mt-5 max-w-3xl leading-relaxed text-slate-600">
            From our early days with the Mahindra Group through to our
            long-standing partnership with 3M, Universal Engineers has
            consistently been recognized for outstanding sales performance and
            customer trust:
          </p>
        </Reveal>
        <Stagger className="mt-10 grid gap-5 md:grid-cols-2" delay={0.06}>
          {AWARDS.map((a) => (
            <StaggerItem key={a.title}>
              <div className="flex h-full items-start gap-5 border border-slate-200 bg-white p-7" data-testid="award-card">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-safety-tint">
                  <a.icon size={24} className="text-safety" strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold leading-snug text-navy">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.text}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Stagger className="mt-6 grid gap-5 md:grid-cols-2" delay={0.08}>
          {CERTIFICATES.map((c) => (
            <StaggerItem key={c.title}>
              <div className="flex h-full flex-col border border-slate-200 bg-white p-7" data-testid="credential-card">
                <div className="flex items-center gap-3.5">
                  <c.icon size={22} className="shrink-0 text-safety" strokeWidth={1.6} />
                  <div>
                    <h3 className="font-display text-base font-bold text-navy">{c.title}</h3>
                    <p className="mt-0.5 text-xs text-slate-500">{c.sub}</p>
                  </div>
                </div>
                <a
                  href={c.img}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={c.testid}
                  className="group relative mt-4 flex flex-1 items-center justify-center overflow-hidden border border-slate-200 bg-slate-50"
                  title="Click to enlarge"
                >
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="max-h-96 w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-navy/90 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <ZoomIn size={12} /> Enlarge
                  </span>
                </a>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <section className="border-t border-slate-200 bg-slate-50 py-20 sm:py-24" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
            <span className="inline-block h-2 w-2 bg-safety" /> Moments &amp; Memories
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            Five decades, in frames.
          </h2>
        </Reveal>
        <Stagger className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3" delay={0.05}>
          {GALLERY.map((g, i) => (
            <StaggerItem key={g.src} className="mb-4 break-inside-avoid">
              <a
                href={g.src}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`gallery-photo-${i + 1}`}
                title="Click to enlarge"
                className="group relative block overflow-hidden bg-navy"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-transparent" />
                <span className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-5 py-4 font-mono text-[10px] uppercase tracking-[0.16em] text-white">
                  <ZoomIn size={13} className="shrink-0 text-safety" />
                  {g.caption}
                </span>
              </a>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <section className="grain relative overflow-hidden bg-navy py-20 sm:py-24" data-testid="about-mission">
      <div className="bp-grid absolute inset-0" />
      <div className="relative z-10 mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-safety">Our Mission &amp; Value Proposition</p>
          <blockquote className="mt-6 font-display text-2xl font-extrabold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="about-mission-quote">
            "Our mission is simple: To empower infrastructure with safe,
            durable, and uninterrupted power performance."
          </blockquote>
          <p className="mx-auto mt-7 max-w-2xl leading-relaxed text-slate-400">
            We bridge the gap between cutting-edge global innovation and
            practical, on-the-ground engineering requirements. When you partner
            with Universal Engineers, you gain access to proven material
            technology backed by a 50-year legacy of integrity, reliability,
            and service excellence.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-20 sm:py-24" data-testid="about-cta">
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            Ready to Power Your Next Project?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-slate-600">
            Whether you require specialized product sourcing, technical
            guidance, or turnkey installation support for LV/HT systems, our
            expert team is ready to assist.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/enquiry" data-testid="about-cta-button" className="group flex items-center gap-2.5 bg-safety px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-navy">
              Send Enquiry <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="about-cta-whatsapp-button" className="border border-slate-300 px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors hover:border-[#25D366] hover:text-[#25D366]">
              WhatsApp Us
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
